import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, ArrowLeft, ImageUp, Wand2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { TID } from "@/constants/tid";
import { VoiceListingButton } from "@/components/VoiceListingButton";
import LogisticsNotice from "@/components/LogisticsNotice";
import { FirstListingGuide } from "@/components/FirstListingGuide";
import { categoryLabel, CATEGORIES } from "@/constants/categories";
import { CURRENCIES } from "@/lib/currency";

const CATS = ["electronics", "phones", "computers", "appliances", "home", "garden", "fashion", "shoes", "bags", "jewelry", "beauty", "health", "baby", "toys", "sports", "automotive", "tools", "agriculture", "office", "grocery", "pets", "books", "music", "art", "digital", "other"];

export default function ProductCreate() {
  const nav = useNavigate();
  const [f, setF] = useState({
    title: "",
    description: "",
    price: "",
    currency: "USD",
    stock: "1",
    category: "electronics",
    tags: "",
    image_url: "",
  });
  const [busy, setBusy] = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  const [aiSummary, setAiSummary] = useState("");
  const [uploading, setUploading] = useState(false);
  const [polishing, setPolishing] = useState(false);
  const [prePolishUrl, setPrePolishUrl] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [isFirst, setIsFirst] = useState(false);
  const [described, setDescribed] = useState(false);
  useEffect(() => {
    api.get("/products/mine").then(({ data }) => setIsFirst((data || []).length === 0)).catch(() => {});
  }, []);

  const aiGeneratePhoto = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/products/bulk/generate-draft-photo", {
        title: f.title,
        category: f.category || "other",
      }, { timeout: 90000 });
      setF((prev) => ({ ...prev, image_url: data.image_url }));
      toast.success("AI product photo ready — replace it anytime with your own");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Generation failed — try again.");
    } finally {
      setGenerating(false);
    }
  };

  const studioPolish = async () => {
    setPolishing(true);
    try {
      const { data } = await api.post("/products/bulk/studio-photo", { image_url: f.image_url });
      setPrePolishUrl(f.image_url);
      setF((prev) => ({ ...prev, image_url: data.image_url }));
      toast.success("Studio shot ready — hit Undo if you prefer the original");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Polish failed — try again.");
    } finally {
      setPolishing(false);
    }
  };
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image too large — max 5MB.");
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/uploads/image", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => {
          if (ev.total) setProgress(Math.round((ev.loaded / ev.total) * 100));
        },
      });
      setF((prev) => ({ ...prev, image_url: data.url }));
      toast.success("Photo uploaded");
      if (!f.description) aiFill(data.url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed — try again.");
    } finally {
      setUploading(false);
    }
  };

  const [assisting, setAssisting] = useState(false);
  const aiFill = async (imageOverride) => {
    const img = typeof imageOverride === "string" ? imageOverride : f.image_url;
    if (!f.title && !img) {
      toast.error("Type a title or upload a photo first — Gemini writes the rest.");
      return;
    }
    setAssisting(true);
    try {
      const { data } = await api.post("/ai/listing-assist", {
        title: f.title,
        notes: f.description,
        image_url: img && img.startsWith("/api/files/") ? img : "",
      });
      setF((prev) => ({
        ...prev,
        title: data.title || prev.title,
        description: data.description,
        category: data.category,
        tags: (data.tags || []).join(", "),
      }));
      setDescribed(true);
      toast.success("Listing written by Gemini — review and tweak as you like");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI fill failed — try again");
    } finally {
      setAssisting(false);
    }
  };

  const enhance = async () => {
    if (!f.title || !f.description) {
      toast.error("Add a title and description first.");
      return;
    }
    setEnhancing(true);
    try {
      const { data } = await api.post("/ai/enhance-description", {
        title: f.title,
        description: f.description,
        category: f.category,
        tags: f.tags ? f.tags.split(",").map((s) => s.trim()).filter(Boolean) : [],
      });
      setAiSummary(data.summary);
      if (data.tags?.length) setF({ ...f, tags: data.tags.join(", ") });
      toast.success("AI summary generated.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setEnhancing(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        title: f.title,
        description: f.description,
        price: parseFloat(f.price),
        currency: f.currency || "USD",
        stock: parseInt(f.stock, 10) || 1,
        category: f.category,
        tags: f.tags ? f.tags.split(",").map((s) => s.trim()).filter(Boolean) : [],
        image_url: f.image_url || null,
      };
      const { data } = await api.post("/products", body);
      // patch ai summary if we have one (via a second call; simpler: create then log)
      toast.success("Product published — it's live to buyers now.");
      nav(`/product/${data.id}`);
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const photoField = (
        <Field label={isFirst ? "Step 1 — your product photo" : "Product photo (optional)"}>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              data-testid="product-image-file"
              onChange={onFilePicked}
            />
            <Button
              type="button"
              variant="outline"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
              data-testid="product-image-upload-btn"
              className="rounded-full bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white"
            >
              <ImageUp className="h-4 w-4 mr-2" />
              {uploading ? `Uploading… ${progress}%` : "Upload photo"}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={generating || !f.title}
              onClick={aiGeneratePhoto}
              data-testid="product-image-generate-btn"
              title={!f.title ? "Enter a title first" : "AI generates a studio product photo from your title"}
              className="rounded-full bg-transparent border-[#FF3B30]/50 text-[#FF3B30] hover:bg-[#FF3B30]/10 hover:text-[#FF3B30]"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              {generating ? "Generating…" : "Generate with AI"}
            </Button>
            <span className="text-[11px] text-neutral-500">JPG / PNG / WEBP · max 5MB — or paste a URL below</span>
          </div>
          {f.image_url && (
            <div className="mt-3 flex items-center gap-3">
              <img
                src={f.image_url}
                alt="Product preview"
                data-testid="product-image-preview"
                className="h-20 w-20 object-cover rounded-md border border-[#27272A]"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              <button type="button" onClick={() => upd("image_url")({ target: { value: "" } })}
                className="text-xs text-neutral-500 hover:text-[#FF3B30]" data-testid="product-image-clear">
                Remove
              </button>
              {f.image_url.startsWith("/api/files/") && (
                <button
                  type="button"
                  onClick={studioPolish}
                  disabled={polishing}
                  data-testid="studio-polish-btn"
                  className="inline-flex items-center gap-1.5 text-xs text-[#FF9F0A] hover:underline disabled:opacity-50"
                >
                  <Wand2 className="h-3.5 w-3.5" /> {polishing ? "Polishing…" : "Studio polish"}
                </button>
              )}
              {prePolishUrl && prePolishUrl !== f.image_url && (
                <button
                  type="button"
                  onClick={() => { setF((prev) => ({ ...prev, image_url: prePolishUrl })); setPrePolishUrl(null); }}
                  data-testid="studio-polish-undo-btn"
                  className="inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-white"
                >
                  <Undo2 className="h-3.5 w-3.5" /> Undo
                </button>
              )}
            </div>
          )}
          <Input data-testid={TID.productImage} value={f.image_url} onChange={upd("image_url")}
            className="bg-[#121212] border-[#27272A] mt-3" placeholder="https://…" />
        </Field>
  );

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-10">
      <Link to="/seller" className="inline-flex items-center gap-2 text-neutral-400 hover:text-white text-sm">
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </Link>
      <h1 className="mt-4 font-black tracking-tight text-4xl">Publish a product</h1>
      <p className="text-neutral-400 mt-2 text-sm">
        Your product goes live to matched buyers the moment you publish — and stays live until you remove it.
      </p>
      <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] text-xs text-neutral-300">
        <span className="h-1.5 w-1.5 rounded-full bg-[#FF3B30]" />
        Platform fee: <span className="font-mono tabular-nums text-white">0.5%</span> per item sold — you keep 99.5%
      </div>

      {isFirst && (
        <FirstListingGuide hasPhoto={!!f.image_url} hasTitle={!!f.title} hasPrice={!!f.price} describing={assisting} described={described} />
      )}

      {!isFirst && <LogisticsNotice className="mt-6" />}

      <form onSubmit={submit} className="mt-8 space-y-5">
        {isFirst && photoField}
        {isFirst && <LogisticsNotice />}

        <Field label="Title">
          <Input required data-testid={TID.productTitle} value={f.title} onChange={upd("title")}
            className="bg-[#121212] border-[#27272A]" placeholder="Onyx Studio Lamp — Limited Run" />
          <button type="button" onClick={aiFill} disabled={assisting} data-testid="ai-fill-btn"
            className="mt-2 h-8 px-3 inline-flex items-center gap-1.5 rounded-full border border-[#007AFF]/50 text-xs font-bold text-[#5ac8fa] hover:border-[#007AFF] hover:text-white transition-colors disabled:opacity-50">
            <Sparkles className="h-3.5 w-3.5" /> {assisting ? "Gemini writing…" : "AI fill — Gemini writes it from your title or photo"}
          </button>

          <div className="mt-2">
            <VoiceListingButton
              onResult={(data) =>
                setF((prev) => ({
                  ...prev,
                  title: data.title || prev.title,
                  description: data.description || prev.description,
                  category: data.category || prev.category,
                  tags: (data.tags || []).join(", ") || prev.tags,
                  price: data.price != null ? String(data.price) : prev.price,
                }))
              }
            />
          </div>
        </Field>

        <Field label="Description">
          <Textarea required rows={5} data-testid={TID.productDesc} value={f.description} onChange={upd("description")}
            className="bg-[#121212] border-[#27272A]" placeholder="Handcrafted matte-black desk lamp with tactile touch controls and adjustable warmth…" />
        </Field>

        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={enhance}
            disabled={enhancing}
            data-testid={TID.productAiEnhance}
            className="rounded-full border-[#007AFF]/50 text-[#007AFF] hover:bg-[#007AFF]/10 hover:text-[#007AFF] bg-transparent"
          >
            <Sparkles className="h-4 w-4 mr-1" />
            {enhancing ? "Enhancing…" : "AI enhance description"}
          </Button>
          {aiSummary && (
            <span className="text-xs text-neutral-500">Summary generated (shown on listing after publish).</span>
          )}
        </div>

        {aiSummary && (
          <div className="rounded-md border border-[#007AFF]/30 bg-[#007AFF]/5 p-4 text-sm text-neutral-200">
            <div className="text-[10px] uppercase tracking-widest text-[#007AFF] mb-1">AI summary preview</div>
            {aiSummary}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label={`Price (${f.currency})`}>
            <div className="flex gap-2">
              <select
                data-testid="product-currency-select"
                value={f.currency}
                onChange={upd("currency")}
                className="h-10 rounded-md bg-[#121212] border border-[#27272A] text-white text-sm px-2 outline-none focus:border-[#007AFF]"
              >
                {CURRENCIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.code}</option>
                ))}
              </select>
              <Input required type="number" step="0.01" min="0.01" data-testid={TID.productPrice}
                value={f.price} onChange={upd("price")} className="bg-[#121212] border-[#27272A] flex-1" placeholder="149.00" />
            </div>
          </Field>
          <Field label="Stock">
            <Input required type="number" min="1" data-testid={TID.productStock}
              value={f.stock} onChange={upd("stock")} className="bg-[#121212] border-[#27272A]" />
          </Field>
          <Field label="Category">
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
              <SelectTrigger data-testid={TID.productCategory} className="bg-[#121212] border-[#27272A]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#27272A] text-white">
                {CATS.map((c) => (
                  <SelectItem key={c} value={c}>
                    <span className="flex flex-col items-start">
                      <span>{categoryLabel(c)}</span>
                      <span className="text-[10px] text-neutral-500">{CATEGORIES.find((x) => x.slug === c)?.subtitle}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <Field label="Tags (comma-separated)">
          <Input data-testid={TID.productTags} value={f.tags} onChange={upd("tags")}
            className="bg-[#121212] border-[#27272A]" placeholder="lighting, minimal, matte-black" />
        </Field>

        {!isFirst && photoField}

        <Button
          type="submit"
          disabled={busy}
          data-testid={TID.productSubmit}
          className="bg-white text-black hover:bg-neutral-200 rounded-full h-11 px-6 font-semibold"
        >
          {busy ? "Publishing…" : "Publish — go live now"}
        </Button>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-neutral-400">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
